import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, User, Lock, Building2, Save, Eye, EyeOff, Camera, Trash2, Loader2, Volume2, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';
const API_URL = `${BACKEND_URL}/api`;

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [userAvatar, setUserAvatar] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const fileInputRef = useRef(null);

  const [voiceSettings, setVoiceSettings] = useState({
    voice: localStorage.getItem('kai_voice') || 'onyx',
    autoSpeak: localStorage.getItem('kai_voice_mode') === 'true',
  });
  const [testingVoice, setTestingVoice] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);

  const [businessProfile, setBusinessProfile] = useState({
    business_type: '',
    tax_form: '',
    industry: '',
    company_size: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);

  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [invoiceData, setInvoiceData] = useState({
    company_name: '',
    nip: '',
    address: '',
    postal_code: '',
    city: '',
    email: '',
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name,
        email: user.email,
      });
    }
    loadInvoiceData();
    loadUserAvatar();
    loadNewsletterStatus();
    loadBusinessProfile();
  }, [user]);

  const loadBusinessProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API_URL}/user/profile-context`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessProfile({
        business_type: resp.data.business_type || '',
        tax_form: resp.data.tax_form || '',
        industry: resp.data.industry || '',
        company_size: resp.data.company_size || '',
      });
    } catch {}
  };

  const handleSaveBusinessProfile = async () => {
    setProfileLoading(true);
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {};
      if (businessProfile.business_type) dataToSend.business_type = businessProfile.business_type;
      if (businessProfile.tax_form) dataToSend.tax_form = businessProfile.tax_form;
      if (businessProfile.industry) dataToSend.industry = businessProfile.industry;
      if (businessProfile.company_size) dataToSend.company_size = businessProfile.company_size;

      await axios.put(`${API_URL}/user/profile-context`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profil firmowy zapisany. KAI dostosuje odpowiedzi do Twojej działalności.');
    } catch {
      toast.error('Błąd zapisu profilu firmowego');
    } finally {
      setProfileLoading(false);
    }
  };

  const loadUserAvatar = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users/avatar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.avatar) {
        setUserAvatar(response.data.avatar);
      }
    } catch (error) {
      // Avatar not set, use default
    }
  };

  const loadNewsletterStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API_URL}/user/newsletter-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewsletterOptIn(resp.data.newsletter_opt_in);
    } catch {}
  };

  const toggleNewsletter = async () => {
    try {
      const token = localStorage.getItem('token');
      const newVal = !newsletterOptIn;
      await axios.post(`${API_URL}/user/newsletter-toggle`, { opt_in: newVal }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewsletterOptIn(newVal);
      toast.success(newVal ? 'Newsletter włączony' : 'Newsletter wyłączony');
    } catch { toast.error('Błąd zmiany ustawień'); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Nieprawidłowy format pliku. Dozwolone: JPG, PNG, GIF, WebP');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Plik jest za duży. Maksymalny rozmiar: 2MB');
      return;
    }

    setAvatarLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/users/avatar`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setUserAvatar(response.data.avatar);
      toast.success('Awatar zaktualizowany');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd przesyłania awatara');
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!window.confirm('Czy na pewno chcesz usunąć swój awatar?')) return;

    setAvatarLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/users/avatar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserAvatar(null);
      toast.success('Awatar usunięty');
    } catch (error) {
      toast.error('Błąd usuwania awatara');
    } finally {
      setAvatarLoading(false);
    }
  };

  const loadInvoiceData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/invoice-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        setInvoiceData(response.data);
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/users/profile`,
        { full_name: profileData.full_name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profil zaktualizowany');
      checkAuth();
    } catch (error) {
      toast.error('Błąd aktualizacji profilu');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Nowe hasła nie są identyczne');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Hasło musi mieć co najmniej 6 znaków');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/users/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Hasło zmienione pomyślnie');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd zmiany hasła');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInvoiceData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/invoice-data`, invoiceData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Dane fakturowe zapisane');
    } catch (error) {
      toast.error('Błąd zapisu danych');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" size="icon" className="dark:text-slate-200 dark:hover:bg-slate-700">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-2xl text-primary-900 dark:text-slate-100 font-bold">Ustawienia</h1>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Avatar Settings */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <Camera className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-primary-900 dark:text-slate-100 font-bold">Awatar</h2>
              <p className="text-sm text-muted-foreground dark:text-slate-400">Twoje zdjęcie profilowe</p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-2xl bg-slate-100 dark:bg-slate-700 overflow-hidden flex items-center justify-center border-2 border-slate-200 dark:border-slate-600">
                {avatarLoading ? (
                  <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                ) : userAvatar ? (
                  <img src={userAvatar} alt="Twój awatar" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-12 h-12 text-slate-300 dark:text-slate-500" />
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleAvatarUpload}
                className="hidden"
                data-testid="avatar-file-input"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarLoading}
                className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl h-10 px-6"
                data-testid="upload-avatar-button"
              >
                <Camera className="w-4 h-4 mr-2" />
                {userAvatar ? 'Zmień awatar' : 'Dodaj awatar'}
              </Button>
              {userAvatar && (
                <Button
                  onClick={handleDeleteAvatar}
                  disabled={avatarLoading}
                  variant="outline"
                  className="border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl h-10 px-6"
                  data-testid="delete-avatar-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Usuń awatar
                </Button>
              )}
              <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">
                Dozwolone formaty: JPG, PNG, GIF, WebP. Maks. 2MB.
              </p>
            </div>
          </div>
        </div>

        {/* Profile Settings */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-primary-900 dark:text-slate-100 font-bold">Dane profilu</h2>
              <p className="text-sm text-muted-foreground dark:text-slate-400">Twoje podstawowe informacje</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="full_name" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Imię i nazwisko</Label>
              <Input
                id="full_name"
                data-testid="full-name-input"
                value={profileData.full_name}
                onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                className="mt-2 h-12 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Email</Label>
              <Input
                id="email"
                value={profileData.email}
                disabled
                className="mt-2 h-12 rounded-xl bg-slate-50 dark:bg-slate-700"
              />
              <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">Email nie może być zmieniony</p>
            </div>
            <Button
              onClick={handleSaveProfile}
              disabled={loading}
              className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl h-12 px-8"
              data-testid="save-profile-button"
            >
              <Save className="w-4 h-4 mr-2" />
              Zapisz zmiany
            </Button>
          </div>
        </div>

        {/* Password Change */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-primary-900 dark:text-slate-100 font-bold">Profil firmowy (AI)</h2>
              <p className="text-sm text-muted-foreground dark:text-slate-400">KAI dostosuje odpowiedzi do Twojej działalności</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Forma prawna</Label>
              <Select
                value={businessProfile.business_type}
                onValueChange={(v) => setBusinessProfile(p => ({ ...p, business_type: v }))}
              >
                <SelectTrigger data-testid="business-type-select" className="mt-2 h-12 rounded-xl dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue placeholder="Wybierz..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="JDG">Jednoosobowa dział. gosp. (JDG)</SelectItem>
                  <SelectItem value="sp. z o.o.">Spółka z o.o.</SelectItem>
                  <SelectItem value="spółka cywilna">Spółka cywilna</SelectItem>
                  <SelectItem value="spółka jawna">Spółka jawna</SelectItem>
                  <SelectItem value="spółka komandytowa">Spółka komandytowa</SelectItem>
                  <SelectItem value="spółka akcyjna">Spółka akcyjna</SelectItem>
                  <SelectItem value="fundacja">Fundacja / Stowarzyszenie</SelectItem>
                  <SelectItem value="inne">Inna</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Forma opodatkowania</Label>
              <Select
                value={businessProfile.tax_form}
                onValueChange={(v) => setBusinessProfile(p => ({ ...p, tax_form: v }))}
              >
                <SelectTrigger data-testid="tax-form-select" className="mt-2 h-12 rounded-xl dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue placeholder="Wybierz..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skala podatkowa">Skala podatkowa (12%/32%)</SelectItem>
                  <SelectItem value="podatek liniowy">Podatek liniowy (19%)</SelectItem>
                  <SelectItem value="ryczałt">Ryczałt ewidencjonowany</SelectItem>
                  <SelectItem value="karta podatkowa">Karta podatkowa</SelectItem>
                  <SelectItem value="CIT 19%">CIT 19%</SelectItem>
                  <SelectItem value="CIT 9%">CIT 9% (mały podatnik)</SelectItem>
                  <SelectItem value="estoński CIT">Estoński CIT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Branża</Label>
              <Select
                value={businessProfile.industry}
                onValueChange={(v) => setBusinessProfile(p => ({ ...p, industry: v }))}
              >
                <SelectTrigger data-testid="industry-select" className="mt-2 h-12 rounded-xl dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue placeholder="Wybierz..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IT">IT / Technologie</SelectItem>
                  <SelectItem value="handel">Handel</SelectItem>
                  <SelectItem value="usługi">Usługi</SelectItem>
                  <SelectItem value="budowlanka">Budownictwo</SelectItem>
                  <SelectItem value="transport">Transport / Logistyka</SelectItem>
                  <SelectItem value="gastronomia">Gastronomia / HoReCa</SelectItem>
                  <SelectItem value="produkcja">Produkcja</SelectItem>
                  <SelectItem value="medycyna">Medycyna / Zdrowie</SelectItem>
                  <SelectItem value="edukacja">Edukacja</SelectItem>
                  <SelectItem value="nieruchomości">Nieruchomości</SelectItem>
                  <SelectItem value="finanse">Finanse / Ubezpieczenia</SelectItem>
                  <SelectItem value="inne">Inna</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Wielkość firmy</Label>
              <Select
                value={businessProfile.company_size}
                onValueChange={(v) => setBusinessProfile(p => ({ ...p, company_size: v }))}
              >
                <SelectTrigger data-testid="company-size-select" className="mt-2 h-12 rounded-xl dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue placeholder="Wybierz..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="jednoosobowa">Jednoosobowa</SelectItem>
                  <SelectItem value="mikro">Mikro (do 10 pracowników)</SelectItem>
                  <SelectItem value="mała">Mała (10-50 pracowników)</SelectItem>
                  <SelectItem value="średnia">Średnia (50-250 pracowników)</SelectItem>
                  <SelectItem value="duża">Duża (250+ pracowników)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground dark:text-slate-400 mt-3">
            Te dane pozwalają KAI personalizować odpowiedzi — np. podawać stawki i limity odpowiednie dla Twojej formy opodatkowania.
          </p>
          <Button
            onClick={handleSaveBusinessProfile}
            disabled={profileLoading}
            className="mt-4 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-xl h-12 px-8"
            data-testid="save-business-profile-button"
          >
            {profileLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Zapisz profil firmowy
          </Button>
        </div>

        {/* Password Change - original */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-primary-900 dark:text-slate-100 font-bold">Zmiana hasła</h2>
              <p className="text-sm text-muted-foreground dark:text-slate-400">Zaktualizuj swoje hasło dostępu</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="current_password" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Obecne hasło</Label>
              <div className="relative mt-2">
                <Input
                  id="current_password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="current-password-input"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  className="h-12 rounded-xl pr-12 dark:bg-slate-700 dark:border-slate-600"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="new_password" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Nowe hasło</Label>
              <div className="relative mt-2">
                <Input
                  id="new_password"
                  type={showNewPassword ? 'text' : 'password'}
                  data-testid="new-password-input"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="h-12 rounded-xl pr-12 dark:bg-slate-700 dark:border-slate-600"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm_password" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Potwierdź nowe hasło</Label>
              <Input
                id="confirm_password"
                type="password"
                data-testid="confirm-password-input"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="mt-2 h-12 rounded-xl dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={loading}
              className="bg-gradient-to-r from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white rounded-xl h-12 px-8"
              data-testid="change-password-button"
            >
              <Lock className="w-4 h-4 mr-2" />
              Zmień hasło
            </Button>
          </div>
        </div>

        {/* Invoice Data */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-primary-900 dark:text-slate-100 font-bold">Dane fakturowe</h2>
              <p className="text-sm text-muted-foreground dark:text-slate-400">Informacje do wystawiania faktur VAT</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="company_name" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Nazwa firmy *</Label>
              <Input
                id="company_name"
                value={invoiceData.company_name}
                onChange={(e) => setInvoiceData({ ...invoiceData, company_name: e.target.value })}
                placeholder="Nazwa Sp. z o.o."
                className="mt-2 h-12 rounded-xl dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
            <div>
              <Label htmlFor="nip" className="text-sm font-semibold text-slate-700 dark:text-slate-300">NIP *</Label>
              <Input
                id="nip"
                value={invoiceData.nip}
                onChange={(e) => setInvoiceData({ ...invoiceData, nip: e.target.value })}
                placeholder="1234567890"
                className="mt-2 h-12 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="invoice_email" className="text-sm font-semibold text-slate-700">Email do faktur *</Label>
              <Input
                id="invoice_email"
                type="email"
                value={invoiceData.email}
                onChange={(e) => setInvoiceData({ ...invoiceData, email: e.target.value })}
                placeholder="faktury@firma.pl"
                className="mt-2 h-12 rounded-xl"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="address" className="text-sm font-semibold text-slate-700">Adres *</Label>
              <Input
                id="address"
                value={invoiceData.address}
                onChange={(e) => setInvoiceData({ ...invoiceData, address: e.target.value })}
                placeholder="ul. Przykładowa 123"
                className="mt-2 h-12 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="postal_code" className="text-sm font-semibold text-slate-700">Kod pocztowy *</Label>
              <Input
                id="postal_code"
                value={invoiceData.postal_code}
                onChange={(e) => setInvoiceData({ ...invoiceData, postal_code: e.target.value })}
                placeholder="00-001"
                className="mt-2 h-12 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="city" className="text-sm font-semibold text-slate-700">Miasto *</Label>
              <Input
                id="city"
                value={invoiceData.city}
                onChange={(e) => setInvoiceData({ ...invoiceData, city: e.target.value })}
                placeholder="Warszawa"
                className="mt-2 h-12 rounded-xl"
              />
            </div>
          </div>
          <Button
            onClick={handleSaveInvoiceData}
            disabled={loading}
            className="mt-6 bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 text-white rounded-xl h-12 px-8"
            data-testid="save-invoice-data-button"
          >
            <Save className="w-4 h-4 mr-2" />
            Zapisz dane fakturowe
          </Button>
        </div>

        {/* Voice Settings */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Volume2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-primary-900 dark:text-slate-100 font-bold">Głos KAI</h2>
              <p className="text-sm text-muted-foreground dark:text-slate-400">Wybierz głos asystenta i ustawienia mowy</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-2 block dark:text-slate-300">Głos asystenta</Label>
              <Select
                value={voiceSettings.voice}
                onValueChange={(v) => {
                  setVoiceSettings(prev => ({ ...prev, voice: v }));
                  localStorage.setItem('kai_voice', v);
                  toast.success('Głos zmieniony');
                }}
              >
                <SelectTrigger data-testid="voice-select" className="dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="onyx">Onyx - głęboki, autorytatywny</SelectItem>
                  <SelectItem value="echo">Echo - spokojny, łagodny</SelectItem>
                  <SelectItem value="ash">Ash - wyraźny, artykułowany</SelectItem>
                  <SelectItem value="fable">Fable - ekspresyjny, narracyjny</SelectItem>
                  <SelectItem value="nova">Nova - energiczny, pogodny</SelectItem>
                  <SelectItem value="shimmer">Shimmer - jasny, radosny</SelectItem>
                  <SelectItem value="coral">Coral - ciepły, przyjazny</SelectItem>
                  <SelectItem value="alloy">Alloy - zrównoważony, neutralny</SelectItem>
                  <SelectItem value="sage">Sage - mądry, wyważony</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium dark:text-slate-300">Auto-odczytywanie odpowiedzi</Label>
                <p className="text-xs text-muted-foreground dark:text-slate-400 mt-0.5">KAI automatycznie odczyta każdą odpowiedź głosowo</p>
              </div>
              <button
                data-testid="auto-speak-toggle"
                onClick={() => {
                  const newVal = !voiceSettings.autoSpeak;
                  setVoiceSettings(prev => ({ ...prev, autoSpeak: newVal }));
                  localStorage.setItem('kai_voice_mode', String(newVal));
                  toast.success(newVal ? 'Auto-odczytywanie włączone' : 'Auto-odczytywanie wyłączone');
                }}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  voiceSettings.autoSpeak ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  voiceSettings.autoSpeak ? 'translate-x-6' : ''
                }`} />
              </button>
            </div>

            <Button
              data-testid="test-voice-btn"
              onClick={async () => {
                setTestingVoice(true);
                try {
                  const token = localStorage.getItem('token');
                  const response = await axios.post(
                    `${API_URL}/voice/synthesize`,
                    { text: 'Witaj, jestem KAI, Twój asystent księgowy. Tak brzmi mój głos.', voice: voiceSettings.voice },
                    { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
                  );
                  const audioUrl = URL.createObjectURL(response.data);
                  const audio = new Audio(audioUrl);
                  audio.onended = () => { setTestingVoice(false); URL.revokeObjectURL(audioUrl); };
                  audio.onerror = () => setTestingVoice(false);
                  await audio.play();
                } catch {
                  toast.error('Nie udało się odtworzyć próbki');
                  setTestingVoice(false);
                }
              }}
              disabled={testingVoice}
              variant="outline"
              className="rounded-xl h-10 px-6 dark:border-slate-600 dark:text-slate-300"
            >
              <Volume2 className={`w-4 h-4 mr-2 ${testingVoice ? 'animate-pulse' : ''}`} />
              {testingVoice ? 'Odtwarzanie...' : 'Przetestuj głos'}
            </Button>
          </div>
        </div>

        {/* Newsletter Settings */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl text-primary-900 dark:text-slate-100 font-bold">Newsletter</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Cotygodniowe podsumowanie zmian w przepisach i nowych artykułów w bazie wiedzy
              </p>
            </div>
            <button
              onClick={toggleNewsletter}
              data-testid="newsletter-toggle"
              className={`relative w-12 h-6 rounded-full transition-colors ${
                newsletterOptIn ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                newsletterOptIn ? 'translate-x-6' : 'translate-x-0'
              }`} />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            {newsletterOptIn ? 'Otrzymujesz newsletter KAI na swój adres email.' : 'Newsletter jest wyłączony.'}
          </p>
        </div>
      </div>
    </div>
  );
}
